import React, {useContext} from "react";
import {ValidatedValue} from "../../../../helpers/validatedValue";
import {observer} from "mobx-react";
import {DateTimePicker as DateTimePickerMui} from "@mui/lab";
import {TextField} from "@mui/material";
import Error from "../error/error";
import {storesContext} from "../../../stores/stores";
import {UserStore} from "../../../../administration/stores/userStore";
import {DateUtils} from "../../../../helpers/dateUtils";

interface DateTimePickerProps {
    onChange?: (date: Date | null) => void;
    validatedValue: ValidatedValue<Date | null | string>;
    label: string;
    testId: string;
    className?: string;
    onClose?: () => void;
    onBlur?: () => void;
}

function DateTimePicker(props: DateTimePickerProps) {
    const userStore = useContext(storesContext).userStore as UserStore;
    const language = userStore.language;
    const mask = DateUtils.dateAndTimeMaskMap[language as keyof typeof DateUtils.dateAndTimeMaskMap];

    return (
        <DateTimePickerMui
            disableMaskedInput={false}
            mask={mask}
            ampm={language === "en"}
            label={props.label}
            value={props.validatedValue.value}
            onChange={(newValue: Date | null) => {
                if (props.onChange) {
                    props.onChange(newValue);
                } else {
                    props.validatedValue.setValue(newValue);
                }
                props.validatedValue.clearValidation();
            }}
            onClose={props.onClose}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    className="date-time-picker text-input"
                    value={props.validatedValue.value || ""}
                    error={displayError()}
                    onChange={onChange}
                    onKeyDown={() => {
                        props.validatedValue.clearValidation();
                    }}
                    onBlur={() => {
                        if (props.onBlur) props.onBlur();
                        props.validatedValue.validate();
                    }}
                    label={props.label}
                    data-testid={props.testId}
                    helperText={getHelperText()}
                    type="datetime"
                    autoComplete="off"
                />
            )}
        />
    );

    function onChange(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
        if (props.validatedValue && props.validatedValue.value) return;
        const text = event.target.value;

        const dateValue = text !== "" ? new Date(text) : null;
        if (props.onChange) {
            props.onChange(dateValue);
        }

        props.validatedValue.setValue(dateValue);
    }

    function displayError(): boolean {
        return !!props.validatedValue.errorMessage;
    }

    function getHelperText() {
        return (displayError() ? <Error message={props.validatedValue.errorMessage}/> : "");
    }
}

export default observer(DateTimePicker);
